import { ReactElement, useState, useRef, useEffect } from "react";
import { Box, Tooltip, SxProps, Theme, useTheme, useMediaQuery } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

interface CannotChangeLaterIconProps {
  /**
   * Tooltip message to display when hovering over the icon
   * This is required to keep the component abstract and reusable
   */
  tooltipMessage: string;
  /**
   * Custom sx styles for the icon
   * @default Uses default positioning styles for Select fields
   */
  sx?: SxProps<Theme>;
  /**
   * Position of the icon
   * @default "select" - positions to the left of dropdown arrow (for Select/FormControl)
   * "inline" - for inline use with custom positioning
   */
  position?: "select" | "inline";
}

/**
 * Reusable Cannot Change Later Icon Component
 * 
 * Displays a warning icon with a customizable tooltip message indicating that
 * a field value cannot be changed after creation/initial setup.
 * 
 * @example
 * // For use with Select/FormControl (default positioning)
 * <FormControl fullWidth required sx={{ position: "relative" }}>
 *   <InputLabel>Currency</InputLabel>
 *   <Select>...</Select>
 *   <CannotChangeLaterIcon 
 *     tooltipMessage="Currency cannot be changed after creation. You can define currencies and rates in Settings."
 *   />
 * </FormControl>
 * 
 * @example
 * // For inline use with custom positioning
 * <CannotChangeLaterIcon 
 *   tooltipMessage="This value cannot be changed later"
 *   position="inline"
 *   sx={{ ml: 1, verticalAlign: "middle" }}
 * />
 */
function CannotChangeLaterIcon({
  tooltipMessage,
  sx,
  position = "select",
}: CannotChangeLaterIconProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, []);

  const defaultSx: SxProps<Theme> =
    position === "select"
      ? {
          fontSize: 18,
          color: "warning.main",
          cursor: "help",
          position: "absolute",
          right: 40,
          top: "50%",
          mt: -0,
          transform: "translateY(-50%)",
          pointerEvents: "auto",
          zIndex: 1,
        }
      : {
          fontSize: 18,
          color: "warning.main",
          cursor: "help",
        };

  const icon = <WarningIcon sx={{ ...defaultSx, ...sx }} />;

  if (isMobile) {
    const handleOpen = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setOpen(true);
      hideTimeoutRef.current = setTimeout(() => setOpen(false), 1000);
    };
    return (
      <Tooltip
        title={tooltipMessage}
        arrow
        placement="top"
        open={open}
        onClose={() => setOpen(false)}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        TransitionProps={{ timeout: 400 }}
      >
        <Box
          component="span"
          onClick={(e) => {
            e.preventDefault();
            handleOpen();
          }}
          sx={{ display: "inline-flex", cursor: "help" }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipMessage} arrow placement="top">
      {icon}
    </Tooltip>
  );
}

export default CannotChangeLaterIcon;
